/**
 * VRG-545 — ONE product-category answer, assembled from the two forms that ask for it.
 *
 * ## The problem this exists to solve
 *
 * vera-website asks "what do you sell?" twice, on two forms, and the answers arrive in two
 * different vocabularies:
 *
 *   - the **waitlist form** (`app/api/request-access/route.ts` → `categories`) sends display
 *     LABELS — `"Handbags"`, `"Wine & Spirits"` — because that payload is read by a human in
 *     a notification email and its own comment says what is drawn must be what is sent;
 *   - the **ROI calculator** (`app/api/roi/route.ts` → `industries`) sends platform SLUGS —
 *     `handbags`, `wine_spirits` — because its value is joined against the platform
 *     vocabulary and travels in a share link.
 *
 * Rendered as two columns, a single row read `handbags` beside `handbags, leather-go…`,
 * which is one visitor's consistent answer presented as two forms disagreeing.
 *
 * ## ⚠️ WHY A UNION AND NOT A CONCATENATION
 *
 * The two submissions are genuinely independent POSTs with independent parses — a visitor
 * who picks Handbags on both forms produces the value twice, and a naive join would show
 * it twice and imply twice the weight. So the union is taken over a NORMALIZED KEY, which
 * is the only thing that makes `handbags` and `"Handbags"` collapse.
 *
 * The website does carry an in-page handoff (`roiHandoffRef` in `RequestAccessModal.tsx`)
 * that pre-selects the calculator's categories from the waitlist form's, translating labels
 * to slugs on the way. That makes the duplicate the COMMON case for anyone who fills both
 * forms in one sitting, not an edge case — but it is a same-session React ref, not
 * persistence, so the two blobs are still independent evidence and neither can be derived
 * from the other. Dedupe on read; do not assume either side is a copy.
 *
 * ## What "canonical" means here, and what happens when a value is not
 *
 * A recognised value resolves to the platform's own display label
 * (`constants/categories.ts`), so the chip reads `Wine & Spirits` rather than
 * `Wine spirits` — and so its icon resolves, since `CategoryCell` keys on a normalization
 * of whatever string it is handed.
 *
 * An UNRECOGNISED value is shown verbatim rather than dropped. A routing answer we do not
 * have a slug for is information — `leather-goods` is live in the dev data today and is a
 * real thing a real brand sells — and dropping it would make the grid quietly disagree with
 * the panel, which renders the blob as sent. It is also the visible half of category drift:
 * a slug appearing here unstyled is the signal that `CATEGORY_TYPES` has fallen behind.
 */
import { CATEGORY_DISPLAY_NAMES, CATEGORY_TYPES } from 'constants/categories'
import type { RoiSubmission, SignupEntry, WaitlistSubmission } from 'hooks/api/useSignups'

/**
 * The comparison key two spellings of one category must agree on.
 *
 * Deliberately the SAME normalization `CategoryCell` and `getCategoryIcon` already apply
 * before an icon lookup (lowercase, runs of non-alphanumerics to `_`), so a value that
 * dedupes here and a value that draws an icon there can never disagree about what counts as
 * the same category. Extended to strip leading/trailing separators, because `"Wine &
 * Spirits"` and `wine_spirits` must land on one key and the ampersand's surrounding spaces
 * would otherwise leave a doubled one.
 *
 * ⚠️ NOT a slug generator. It produces a key for MATCHING; the string a reviewer reads is
 * always either the platform's own label or the visitor's own words, never this.
 */
export const categoryKey = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

/**
 * Every spelling the platform itself recognises → the label to show for it.
 *
 * Built from BOTH halves of `constants/categories.ts` — the slug and its display name —
 * because the two forms send one each and neither is more canonical than the other on the
 * wire. Derived rather than listed, so a category added upstream is matched here with no
 * further edit.
 */
const CANONICAL_LABEL_BY_KEY: Record<string, string> = (() => {
  const out: Record<string, string> = {}
  for (const slug of CATEGORY_TYPES) {
    const label = CATEGORY_DISPLAY_NAMES[slug] || slug
    out[categoryKey(slug)] = label
    out[categoryKey(label)] = label
  }
  return out
})()

/**
 * Categories vera-website offers that the platform has NO slug for.
 *
 * One member, and it is deliberate on the website's side rather than drift: its
 * `content/product-categories.ts` declares `AUCTION_RESALE` as the single `websiteOnly`
 * entry, flagged so that repo's own mirror-drift script does not report it as a
 * divergence from `CATEGORY_TYPES`.
 *
 * ⚠️ IT IS LISTED HERE FOR ONE REASON: the two forms spell it differently, like every other
 * category — the waitlist sends the label, the calculator sends `website_auction_resale` —
 * and with no entry the normalization above cannot know they are the same answer, so a
 * visitor who picked it on both forms gets TWO chips for one choice. That is the exact
 * double-count this module exists to prevent, so leaving it to the unrecognised path would
 * mean fixing the general case and shipping the known exception broken.
 *
 * A category that appears here and later gains a real `CATEGORY_TYPES` slug should be
 * removed from this map in the same change: the derived map above would then answer for it,
 * and two sources for one label is how the drift starts.
 */
const WEBSITE_ONLY_LABEL_BY_KEY: Record<string, string> = {
  website_auction_resale: 'Auction & Resale Markets',
  [categoryKey('Auction & Resale Markets')]: 'Auction & Resale Markets',
}

/** A submitted value → what the chip should read, or `null` if we have no label for it. */
export const canonicalCategoryLabel = (value: string): string | null => {
  const key = categoryKey(value)
  if (!key) return null
  return CANONICAL_LABEL_BY_KEY[key] ?? WEBSITE_ONLY_LABEL_BY_KEY[key] ?? null
}

/** The string entries of a blob's list field, ignoring blanks and non-strings. */
const listOf = (
  blob: RoiSubmission | WaitlistSubmission | null | undefined,
  key: string,
): string[] => {
  const raw = blob?.[key]
  if (Array.isArray(raw)) {
    return raw.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
  }
  // Same shape-change tolerance as `websiteListItems`: a scalar where an array was expected
  // is surfaced rather than dropped, so a website change is visible instead of silent.
  return typeof raw === 'string' && raw.trim() ? [raw] : []
}

/**
 * Every product category this signup told us about, from either form, each once.
 *
 * Order is WAITLIST FIRST, then the calculator's additions, in submitted order. Not sorted:
 * both forms emit in their own chip order rather than in click order (verified on the
 * website side, where that choice is commented as avoiding the appearance of a ranking), so
 * a sort here would replace one deliberate order with an arbitrary one. First-seen wins the
 * spelling, but only the KEY decides identity, so which form arrived first cannot change
 * how a recognised category reads.
 */
export const mergedCategories = (row: SignupEntry): string[] => {
  const submitted = [
    ...listOf(row.waitlist_submission, 'categories'),
    ...listOf(row.roi_submission, 'industries'),
  ]

  const out: string[] = []
  const seen = new Set<string>()
  for (const value of submitted) {
    const key = categoryKey(value)
    if (!key) continue
    // The platform's label when we know it; the visitor's own words when we do not.
    const label = canonicalCategoryLabel(value) ?? value.trim()
    /*
      ⚠️ DEDUPE ON THE LABEL WE ARE ABOUT TO SHOW, not on the key we matched by.

      Two values can normalize to DIFFERENT keys and still resolve to the SAME label —
      `website_auction_resale` and `"Auction & Resale Markets"` are the live pair, and they
      are exactly the pair a visitor produces by picking that category on both forms. Keying
      the dedupe on the normalized input let both through, and the cell rendered the same
      chip twice: a duplicate that is not merely wrong but INVISIBLE as a bug, because both
      chips look correct.

      The label is what a reader sees, so the label is the identity. Caught by driving a
      probe row through both endpoints; the seeded row could not have caught it, since its
      only overlap (`handbags`/`handbags`) agrees on the key too.
    */
    if (seen.has(label)) continue
    seen.add(label)
    out.push(label)
  }
  return out
}
